/**
 * Ported from `packages/ja-core/src/morphology.ts` in `git@git.fugl.dev:sks/yominoki.git`
 * @ `515ce239`. Bindery is canonical for this module from 2026-09-06; reconcile yominoki-side
 * drift toward this copy.
 */

// Verb / adjective morphological factoring for card rendering.
//
// `splitVerb` and `splitAdjective` factor a dictionary form into a stem
// and inflectional ending so the card can render the stem in --ink and
// the ending in --ink-soft. POS strings are open-ended (kiri / unidic);
// classification falls back to UniDic surface tags (動詞, 形容詞, 形状詞)
// and to the trailing kana when present.
//
// Pure functions, colocated interfaces — same shape as decay.ts and
// tokenize.ts. No factories, no singletons.

export type VerbKind = "consonant" | "vowel" | "irregular";

export interface VerbSplit {
  /** Orthographic stem — everything before the final hiragana of the dictionary form. May be empty for bare irregulars (`する`, `来る`). */
  readonly stem: string;
  /** Trailing hiragana (for godan/ichidan: one char; for compound `〜する`: `する`). Empty for bare `する`/`来る` after the irregular branch. */
  readonly ending: string;
  readonly kind: VerbKind;
  /** Linguistic romaji split, only set when a kana reading is supplied. */
  readonly romaji?: { stem: string; ending: string };
}

export type AdjectiveKind = "い" | "な";

export interface AdjectiveSplit {
  /** For い-adjectives: dictionary form minus trailing い. For な-adjectives: the full dictionary form (な never appears in JMdict citation form). */
  readonly stem: string;
  /** `い` for い-adjectives, empty string for な-adjectives. */
  readonly ending: string;
  readonly kind: AdjectiveKind;
}

const HIRAGANA_RE = /[ぁ-ゟ]/;

function isHiragana(ch: string): boolean {
  return HIRAGANA_RE.test(ch);
}

function isVerbPos(pos: string): boolean {
  if (!pos) return false;
  if (pos.includes("動詞")) return true;
  const lower = pos.toLowerCase();
  return lower.startsWith("v-") || lower === "v" || lower.startsWith("verb");
}

function isIchidanPos(pos: string): boolean {
  const lower = pos.toLowerCase();
  return lower.includes("ichidan") || pos.includes("一段");
}

function isGodanPos(pos: string): boolean {
  const lower = pos.toLowerCase();
  return lower.includes("godan") || pos.includes("五段");
}

function isIAdjPos(pos: string): boolean {
  if (!pos) return false;
  if (pos.includes("形容詞")) return true;
  const lower = pos.toLowerCase();
  return lower.includes("i-adjective") || lower.includes("i adjective");
}

function isNaAdjPos(pos: string): boolean {
  if (!pos) return false;
  if (pos.includes("形状詞")) return true;
  const lower = pos.toLowerCase();
  return lower.includes("na-adjective") || lower.includes("na adjective");
}

/**
 * Factor a verb dictionary form into stem + inflectional ending.
 *
 * - `する` / `来る` / `くる` are irregular: bare forms return empty stem +
 *   the whole dictionary form as ending; compound `〜する` (e.g. `勉強する`)
 *   returns the prefix as stem + `する` as ending.
 * - Vowel-stem (一段) verbs: stem is dictionary form minus trailing る.
 * - Consonant-stem (五段) verbs: stem is dictionary form minus trailing
 *   hiragana; ending is that hiragana.
 *
 * `reading` (hiragana/katakana) enables the linguistic romaji split:
 *   consonant-stem `oyog-` + `-u`, vowel-stem `tabe-` + `-ru`.
 * Kana with kanji in the dictionary form is fine — romaji is derived
 * from the supplied reading, not the orthography.
 */
export function splitVerb(
  dictionaryForm: string,
  pos: string,
  reading?: string,
): VerbSplit {
  // Irregulars first — する / 来る / くる.
  if (dictionaryForm === "する") {
    return {
      stem: "",
      ending: "する",
      kind: "irregular",
      romaji: reading ? splitIrregularRomaji(reading, "suru") : undefined,
    };
  }
  if (dictionaryForm === "来る" || dictionaryForm === "くる") {
    return {
      stem: "",
      ending: dictionaryForm,
      kind: "irregular",
      romaji: reading ? splitIrregularRomaji(reading, "kuru") : undefined,
    };
  }
  if (dictionaryForm.endsWith("する") && dictionaryForm.length > 2) {
    const stem = dictionaryForm.slice(0, -2);
    const stemReading = reading?.endsWith("する")
      ? reading.slice(0, -2)
      : undefined;
    return {
      stem,
      ending: "する",
      kind: "irregular",
      romaji: stemReading
        ? { stem: kanaToRomaji(stemReading), ending: "suru" }
        : undefined,
    };
  }
  if (
    (dictionaryForm.endsWith("来る") || dictionaryForm.endsWith("くる")) &&
    dictionaryForm.length > 2
  ) {
    const stem = dictionaryForm.slice(0, -2);
    return {
      stem,
      ending: dictionaryForm.slice(-2),
      kind: "irregular",
    };
  }

  const lastChar = dictionaryForm.slice(-1);
  if (!isHiragana(lastChar)) {
    // Non-conjugating surface (no trailing kana). Treat as godan with
    // empty ending — the helper falls through to plain rendering.
    return { stem: dictionaryForm, ending: "", kind: "consonant" };
  }

  const stem = dictionaryForm.slice(0, -1);
  let kind: VerbKind = isIchidanPos(pos) ? "vowel" : "consonant";
  if (!isIchidanPos(pos) && !isGodanPos(pos)) {
    // POS didn't disambiguate. Heuristic: if dictionary form ends in る
    // AND the kana before it is in the e/i row, lean ichidan; otherwise
    // godan. Imperfect (e.g. 帰る is godan despite ending -eru) but the
    // POS-tagged path is the primary one.
    if (lastChar === "る") {
      const prev = stem.slice(-1);
      kind = ICHIDAN_PRECEDERS.has(prev) ? "vowel" : "consonant";
    }
  }

  const romaji = reading ? deriveRomaji(reading, kind, lastChar) : undefined;

  return { stem, ending: lastChar, kind, romaji };
}

/**
 * Factor an adjective dictionary form into stem + ending.
 *
 * - い-adjectives: stem = form without trailing い, ending = `い`.
 * - な-adjectives: stem = the whole dictionary form, ending = empty
 *   (`な` is attributive only and never appears in citation form).
 *
 * If POS doesn't classify but the form ends in `い`, falls through to
 * い-adjective treatment so `高い` works even without POS data.
 */
export function splitAdjective(
  dictionaryForm: string,
  pos: string,
): AdjectiveSplit {
  if (isNaAdjPos(pos)) {
    return { stem: dictionaryForm, ending: "", kind: "な" };
  }
  // i-adjective citation forms always end in い in JMdict; treat the い
  // suffix alone as the trigger so a missing/ambiguous POS still works.
  if (dictionaryForm.endsWith("い") && dictionaryForm.length > 1) {
    return {
      stem: dictionaryForm.slice(0, -1),
      ending: "い",
      kind: "い",
    };
  }
  return { stem: dictionaryForm, ending: "", kind: "な" };
}

/** True when POS identifies a verb. */
export function isVerb(pos: string): boolean {
  return isVerbPos(pos);
}

/** True when POS identifies an adjective (either kind). */
export function isAdjective(pos: string): boolean {
  return isIAdjPos(pos) || isNaAdjPos(pos);
}

// ---------- internals ----------

const ICHIDAN_PRECEDERS = new Set([
  // e-row
  "え",
  "け",
  "げ",
  "せ",
  "ぜ",
  "て",
  "で",
  "ね",
  "へ",
  "べ",
  "ぺ",
  "め",
  "れ",
  // i-row
  "い",
  "き",
  "ぎ",
  "し",
  "じ",
  "ち",
  "ぢ",
  "に",
  "ひ",
  "び",
  "ぴ",
  "み",
  "り",
]);

function splitIrregularRomaji(
  reading: string,
  expectFull: "suru" | "kuru",
): { stem: string; ending: string } | undefined {
  const full = kanaToRomaji(reading);
  if (full === expectFull) return { stem: "", ending: expectFull };
  // Compound or katakana variant — best effort.
  return undefined;
}

function deriveRomaji(
  reading: string,
  kind: VerbKind,
  lastKana: string,
): { stem: string; ending: string } | undefined {
  if (reading.length < 1) return undefined;
  const stemKana = reading.slice(0, -1);
  const endingKana = reading.slice(-1);
  if (kind === "vowel") {
    return {
      stem: kanaToRomaji(stemKana),
      ending: kanaToRomaji(endingKana),
    };
  }
  // consonant-stem: peel the consonant off the last kana onto the stem.
  const last = GODAN_LAST.get(lastKana);
  if (!last) {
    return {
      stem: kanaToRomaji(stemKana),
      ending: kanaToRomaji(endingKana),
    };
  }
  const stem = kanaToRomaji(stemKana) + last.consonant;
  return { stem, ending: last.vowel };
}

// Last-kana split for godan endings: consonant onto stem, vowel as ending.
const GODAN_LAST = new Map<string, { consonant: string; vowel: string }>([
  ["う", { consonant: "", vowel: "u" }],
  ["く", { consonant: "k", vowel: "u" }],
  ["ぐ", { consonant: "g", vowel: "u" }],
  ["す", { consonant: "s", vowel: "u" }],
  ["つ", { consonant: "ts", vowel: "u" }],
  ["ぬ", { consonant: "n", vowel: "u" }],
  ["ぶ", { consonant: "b", vowel: "u" }],
  ["む", { consonant: "m", vowel: "u" }],
  ["る", { consonant: "r", vowel: "u" }],
]);

// Hepburn-ish kana → romaji. Covers all gojūon + dakuten/handakuten +
// the common yōon. Long-vowel macrons are rendered as plain digraphs
// (`ou`, `uu`) — keeps the stem readable without a Unicode combining
// pass. Sokuon (っ) is rare in dictionary-form verbs/adjectives; if it
// appears mid-stem it renders as `tt` against the next consonant.
const KANA_TABLE: Record<string, string> = {
  あ: "a",
  い: "i",
  う: "u",
  え: "e",
  お: "o",
  か: "ka",
  き: "ki",
  く: "ku",
  け: "ke",
  こ: "ko",
  さ: "sa",
  し: "shi",
  す: "su",
  せ: "se",
  そ: "so",
  た: "ta",
  ち: "chi",
  つ: "tsu",
  て: "te",
  と: "to",
  な: "na",
  に: "ni",
  ぬ: "nu",
  ね: "ne",
  の: "no",
  は: "ha",
  ひ: "hi",
  ふ: "fu",
  へ: "he",
  ほ: "ho",
  ま: "ma",
  み: "mi",
  む: "mu",
  め: "me",
  も: "mo",
  や: "ya",
  ゆ: "yu",
  よ: "yo",
  ら: "ra",
  り: "ri",
  る: "ru",
  れ: "re",
  ろ: "ro",
  わ: "wa",
  ゐ: "wi",
  ゑ: "we",
  を: "wo",
  ん: "n",
  が: "ga",
  ぎ: "gi",
  ぐ: "gu",
  げ: "ge",
  ご: "go",
  ざ: "za",
  じ: "ji",
  ず: "zu",
  ぜ: "ze",
  ぞ: "zo",
  だ: "da",
  ぢ: "ji",
  づ: "zu",
  で: "de",
  ど: "do",
  ば: "ba",
  び: "bi",
  ぶ: "bu",
  べ: "be",
  ぼ: "bo",
  ぱ: "pa",
  ぴ: "pi",
  ぷ: "pu",
  ぺ: "pe",
  ぽ: "po",
};

const YOON_TABLE: Record<string, string> = {
  きゃ: "kya",
  きゅ: "kyu",
  きょ: "kyo",
  しゃ: "sha",
  しゅ: "shu",
  しょ: "sho",
  ちゃ: "cha",
  ちゅ: "chu",
  ちょ: "cho",
  にゃ: "nya",
  にゅ: "nyu",
  にょ: "nyo",
  ひゃ: "hya",
  ひゅ: "hyu",
  ひょ: "hyo",
  みゃ: "mya",
  みゅ: "myu",
  みょ: "myo",
  りゃ: "rya",
  りゅ: "ryu",
  りょ: "ryo",
  ぎゃ: "gya",
  ぎゅ: "gyu",
  ぎょ: "gyo",
  じゃ: "ja",
  じゅ: "ju",
  じょ: "jo",
  ぢゃ: "ja",
  ぢゅ: "ju",
  ぢょ: "jo",
  びゃ: "bya",
  びゅ: "byu",
  びょ: "byo",
  ぴゃ: "pya",
  ぴゅ: "pyu",
  ぴょ: "pyo",
};

function katakanaToHiragana(ch: string): string {
  const code = ch.charCodeAt(0);
  if (code >= 0x30a1 && code <= 0x30f6) {
    return String.fromCharCode(code - 0x60);
  }
  return ch;
}

function kanaToRomaji(input: string): string {
  if (!input) return "";
  // Normalize katakana → hiragana for table lookup.
  const normalized = Array.from(input).map(katakanaToHiragana).join("");
  let out = "";
  let i = 0;
  while (i < normalized.length) {
    const ch = normalized[i]!;
    const next = normalized[i + 1];
    if (next && (next === "ゃ" || next === "ゅ" || next === "ょ")) {
      const pair = ch + next;
      const yoon = YOON_TABLE[pair];
      if (yoon) {
        out += yoon;
        i += 2;
        continue;
      }
    }
    if (ch === "っ") {
      // Sokuon: double the next consonant (or render as 't' on its own).
      const after = next
        ? (KANA_TABLE[next] ?? YOON_TABLE[next + (normalized[i + 2] ?? "")])
        : null;
      if (after) {
        out += after[0];
      } else {
        out += "t";
      }
      i += 1;
      continue;
    }
    if (ch === "ー") {
      // Long-vowel mark — repeat the previous vowel.
      const prev = out.slice(-1);
      if (prev) out += prev;
      i += 1;
      continue;
    }
    out += KANA_TABLE[ch] ?? ch;
    i += 1;
  }
  return out;
}
